import React, { useState, useMemo, useEffect } from 'react';
import { Button } from './button';
import {
  Maximize2, ShoppingCart, Lock, Unlock, Eye, X, Loader, Loader2,
  Star, Pin, PinOff, Columns2, Share2,
} from 'lucide-react';
import { ShareStyleDialog } from './sharedstyles';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from './tooltip';
import {
  Dialog, DialogContent, DialogTrigger,
} from './dialog';
import MapPurchaseSection from './mappurchuase';
import { useToast } from './use-toast';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

// Compare mode shows the *middle* slice of every image side by side. With
// the comparison container set to the source aspect ratio and each strip at
// 1/N of its width, `object-cover` + `object-center` exposes the central 1/N
// band of each image — middle half for N=2, middle third for N=3.

const RecentMaps = ({
  recentMaps,
  onEnlarge,
  handleSizeSelect,
  isUnlocking,
  setVoucher,
  selectedFormat,
  isPortrait,
  setSelectedTab,
  refreshRecentMaps,
}) => {
  const { toast } = useToast();

  // The backend returns the last 5 by created_at + every pinned map (deduped).
  // Split into two rows: the recent grid stays sorted by created_at, the
  // pinned row only shows entries with pinned=true. They may overlap by
  // design — a pinned map among the last 5 appears in both rows.
  const recentList = useMemo(() => {
    if (!recentMaps) return [];
    return [...recentMaps]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5);
  }, [recentMaps]);

  const pinnedList = useMemo(() => {
    if (!recentMaps) return [];
    return recentMaps.filter((m) => m.pinned);
  }, [recentMaps]);

  const mostRecentId = recentList[0]?.id ?? null;

  const [selectedId, setSelectedId] = useState(mostRecentId);
  const [isPurchaseOpen, setIsPurchaseOpen] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  // image ids selected for comparison, in click order. Cap = 3.
  const [compareIds, setCompareIds] = useState([]);
  const [pinningId, setPinningId] = useState(null); // id currently being toggled
  const [compareEnlarged, setCompareEnlarged] = useState(false);
  const [shareDialogMap, setShareDialogMap] = useState(null);

  useEffect(() => {
    setSelectedId(mostRecentId);
  }, [mostRecentId]);

  // Look up by id across the union of recent + pinned so the featured area
  // can render either.
  const findMap = (id) => recentMaps?.find((m) => m.id === id) || null;

  const selectedMap = findMap(selectedId) || recentList[0] || null;
  const compareMaps = compareIds.map(findMap).filter(Boolean);

  // Aspect ratio of the comparison container = aspect ratio of the source
  // images. Without this, a portrait source forced into a landscape box
  // makes object-cover crop oddly and the combined view collapses in height.
  const compareAspect = useMemo(() => {
    if (compareMaps.length === 0) return '4 / 3';
    const m = compareMaps[0];
    let w = 4;
    let h = 3;
    const v = m?.format?.value;
    if (typeof v === 'string') {
      const parts = v.split(':').map(Number);
      if (parts.length === 2 && parts[0] > 0 && parts[1] > 0) {
        [w, h] = parts;
      }
    }
    return m?.isPortrait ? `${h} / ${w}` : `${w} / ${h}`;
  }, [compareMaps]);

  const formatDate = (isoString) => {
    try {
      const date = new Date(isoString);
      return new Intl.DateTimeFormat('default', {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      }).format(date);
    } catch (_e) {
      return 'Date unavailable';
    }
  };

  const togglePin = async (image_id, nextPinned, kind = 'recent') => {
    if (pinningId) return; // single-flight per click burst
    setPinningId(image_id);
    try {
      // Permanent (paid) images use a different pin endpoint; recent_images
      // entries use the /recent_maps one. Both share the same 3-slot cap.
      const url = kind === 'image'
        ? `${API_URL}/user/images/${image_id}/pin`
        : `${API_URL}/recent_maps/${image_id}/pin`;
      const r = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pinned: nextPinned }),
      });
      if (r.status === 409) {
        toast({
          title: 'Pin limit reached',
          description: 'You can pin up to 3 maps. Unpin one to free a slot.',
          className: 'bg-amber-200 text-black',
          duration: 4000,
        });
        return;
      }
      if (!r.ok) throw new Error('Pin update failed');
      if (refreshRecentMaps) await refreshRecentMaps();
    } catch (_e) {
      toast({
        title: 'Could not update pin',
        description: 'Please try again.',
        className: 'bg-red-300 text-black',
        duration: 3000,
      });
    } finally {
      setPinningId(null);
    }
  };

  const toggleCompareSelect = (id) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) {
        toast({
          title: 'Compare limit',
          description: 'You can compare up to 3 maps at once.',
          className: 'bg-amber-200 text-black',
          duration: 2500,
        });
        return prev;
      }
      return [...prev, id];
    });
  };

  const exitCompareMode = () => {
    setCompareMode(false);
    setCompareIds([]);
  };

  if (!recentMaps || recentMaps.length === 0) {
    return (
      <div className="w-full max-w-4xl mx-auto space-y-6">
        <div className="w-full max-w-4xl mx-auto aspect-[4/3] bg-gray-100 rounded-lg flex items-center justify-center">
          <p className="text-gray-400">No maps generated yet</p>
        </div>
      </div>
    );
  }

  // ----- compare strip rendering -----
  // Each strip occupies 1/N of the comparison row and shows ONLY the band of
  // the source image that matches its position (left third of img 1, middle
  // third of img 2, right third of img 3). Implemented via object-cover +
  // object-position so the strip's narrow aspect ratio crops horizontally.
  const compareStripsInner = (className = '') => {
    const N = compareMaps.length;
    return (
      <div
        className={`flex gap-1 rounded-lg overflow-hidden shadow-2xl shadow-black/50 ${className}`}
        style={{ aspectRatio: compareAspect }}
      >
        {compareMaps.map((m, i) => (
          <div key={m.id} className="flex-1 h-full overflow-hidden bg-white">
            <img
              src={m.imageUrl}
              alt={`Compare slice ${i + 1}`}
              className="w-full h-full object-cover object-center"
            />
          </div>
        ))}
      </div>
    );
  };

  const renderCompareStrips = () => {
    const N = compareMaps.length;
    return (
      <div className="relative group bg-gray-200 rounded-xl p-4 overflow-hidden">
        {compareStripsInner('w-full max-h-[40vh] mx-auto')}
        <div className="absolute top-2 left-2 bg-blue-600 text-white px-3 py-1 rounded-xl text-sm font-medium">
          Comparing {N} maps
        </div>
        <div className="absolute bottom-2 right-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="default"
                  size="icon"
                  className="h-8 w-8 bg-gray-900 shadow-xl border-0 text-white hover:bg-gray-600 rounded-2xl"
                  onClick={() => setCompareEnlarged(true)}
                >
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>View larger comparison</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-4">
      {/* Featured area: comparison strips when ≥2 selected, otherwise the
          single selected map */}
      {compareMode && compareMaps.length >= 2 ? (
        renderCompareStrips()
      ) : selectedMap ? (
        <div className="relative group flex bg-gray-200 rounded-xl p-4 overflow-hidden items-center justify-center">
          <img
            src={selectedMap.imageUrl}
            alt={`Generated map ${selectedMap.id}`}
            className="max-h-[40vh] object-scale-down shadow-2xl shadow-black/50"
          />
          <div className="absolute top-2 left-2 flex flex-col gap-2 items-start">
            {!selectedMap.locked && (
              <Button
                size="sm"
                onClick={() => setShareDialogMap(selectedMap)}
                className="bg-gray-900 shadow-xl border-0 text-white hover:bg-gray-600 rounded-2xl flex items-center gap-2"
              >
                <Share2 className="h-4 w-4" />
                Share style
              </Button>
            )}
            {selectedMap.id === mostRecentId && (
              <div className="bg-red-500 text-white px-3 py-1 rounded-xl text-sm font-medium">
                Latest
              </div>
            )}
          </div>
          <div className="absolute top-2 right-2 bg-gray-900/75 text-white px-3 py-1 rounded-xl text-sm font-medium flex items-center gap-2">
            {selectedMap.locked ? (
              <><Lock className="w-4 h-4" /><span>Locked</span></>
            ) : (
              <><Unlock className="w-4 h-4" /><span>Unlocked</span></>
            )}
          </div>
          <div className="absolute bottom-2 right-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="default"
                    size="icon"
                    className="h-8 w-8 bg-gray-900 shadow-xl border-0 text-white hover:bg-gray-600 rounded-2xl"
                    onClick={() => onEnlarge({ imageUrl: selectedMap.imageUrl })}
                  >
                    <Maximize2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View larger image</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="absolute bottom-2 left-2 bg-gray-900/75 text-white px-3 py-1 rounded-xl text-sm">
            {formatDate(selectedMap.created_at)}
          </div>
        </div>
      ) : null}

      {/* Purchase / View button — hidden in compare mode */}
      {!compareMode && selectedMap && (
        <div className="flex justify-center">
          {selectedMap.locked ? (
            <Dialog open={isPurchaseOpen} onOpenChange={setIsPurchaseOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gray-900 shadow-xl border-0 text-white hover:bg-gray-600 rounded-2xl">
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  Unlock Map
                </Button>
              </DialogTrigger>
              <DialogContent className="w-full max-w-lg items-center justify-center">
                <MapPurchaseSection
                  finalImage={selectedMap.imageUrl}
                  maxDpis={selectedMap.max_dpis}
                  selectedFormat={selectedMap.format || selectedFormat}
                  onSizeSelect={(size, dimensions, needsRegeneration, voucherCode) => {
                    handleSizeSelect(selectedMap.id, size, dimensions, needsRegeneration, voucherCode);
                    setIsPurchaseOpen(false);
                  }}
                  isUnlocking={isUnlocking}
                  isPortrait={selectedMap.isPortrait ?? isPortrait}
                  km2={selectedMap.km2}
                  voucher={setVoucher}
                />
              </DialogContent>
            </Dialog>
          ) : (
            <Button
              className="bg-gray-900 shadow-xl border-0 text-white hover:bg-gray-600 rounded-2xl"
              onClick={() => setSelectedTab('history')}
            >
              <Eye className="mr-2 h-5 w-5" />
              View and Download
            </Button>
          )}
        </div>
      )}

      {/* Recent grid (last 5 by date, may include pinned ones) */}
      <div>
        <div className="grid grid-cols-5 gap-2 md:gap-3">
          {recentList.map((map) => {
            const isCompareSelected = compareIds.includes(map.id);
            const isFeatured = !compareMode && map.id === selectedId;
            const isLatest = map.id === mostRecentId;
            return (
              <div
                key={map.id}
                className={`relative cursor-pointer group bg-gray-50 rounded-lg overflow-hidden transition-all duration-200 ${isCompareSelected ? 'ring-2 ring-blue-500 scale-100'
                    : isFeatured ? 'ring-2 ring-blue-500'
                      : 'hover:ring-2 hover:ring-gray-300'
                  } ${isLatest && !isCompareSelected && !isFeatured ? 'ring-2 ring-red-500' : ''}`}
                onClick={() => {
                  if (compareMode) toggleCompareSelect(map.id);
                  else setSelectedId(map.id);
                }}
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={map.imageUrl}
                    alt={`Generated map ${map.id}`}
                    className="w-full h-full object-cover"
                  />
                  {map.locked && (
                    <Lock className="absolute top-1 right-1 w-4 h-4 text-gray-900/75" />
                  )}
                </div>
                {isLatest && (
                  <div className="absolute top-1 left-1 bg-red-500 text-white px-2 py-0.5 rounded text-xs font-medium">
                    Latest
                  </div>
                )}
                {/* Pin button — only when NOT in compare mode */}
                {!compareMode && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePin(map.id, !map.pinned);
                    }}
                    disabled={pinningId === map.id}
                    title={map.pinned ? 'Unpin' : 'Pin'}
                    className="absolute bottom-1 right-1 h-7 w-7 rounded-full bg-gray-900/75 hover:bg-gray-900 text-white flex items-center justify-center"
                  >
                    {map.pinned
                      ? <Pin className="h-3.5 w-3.5 fill-yellow-300 text-yellow-300" />
                      : <Pin className="h-3.5 w-3.5" />
                    }
                  </button>
                )}
                {/* Compare check overlay */}
                {compareMode && isCompareSelected && (
                  <div className="absolute top-1 right-1 h-6 w-6 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-xs">
                    {compareIds.indexOf(map.id) + 1}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-gray-500 mt-2">Last five generated maps</p>
      </div>

      {/* Compare-mode controls — between the recent grid and the pinned row */}
      <div className="flex justify-center items-center gap-2">
        {compareMode ? (
          <>
            <span className="text-sm text-gray-600">
              {compareIds.length}/3 selected
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={exitCompareMode}
              className="rounded-2xl"
            >
              <X className="mr-1 h-4 w-4" />
              Exit compare
            </Button>
          </>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCompareMode(true)}
            className="rounded-2xl"
          >
            <Columns2 className="mr-1 h-4 w-4" />
            Compare
          </Button>
        )}
      </div>

      {/* Pinned row — three small fixed-size slots; empty slots show a star */}
      <div>
        <div className="flex gap-2 justify-center">
          {Array.from({ length: 3 }).map((_, i) => {
            const map = pinnedList[i];
            if (!map) {
              return (
                <div
                  key={`pin-empty-${i}`}
                  className="w-24 aspect-[4/3] rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center text-gray-400"
                >
                  <Star className="h-5 w-5 mb-0.5" />
                  <span className="text-[10px]">Pin slot {i + 1}</span>
                </div>
              );
            }
            const isCompareSelected = compareIds.includes(map.id);
            const isFeatured = !compareMode && map.id === selectedId;
            return (
              <div
                key={map.id}
                className={`relative cursor-pointer group bg-gray-50 rounded-lg overflow-hidden transition-all duration-200 w-24 ${isCompareSelected ? 'ring-2 ring-blue-500'
                    : isFeatured ? 'ring-2 ring-blue-500'
                      : 'hover:ring-2 hover:ring-gray-300'
                  }`}
                onClick={() => {
                  if (compareMode) toggleCompareSelect(map.id);
                  else setSelectedId(map.id);
                }}
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={map.imageUrl}
                    alt={`Pinned map ${map.id}`}
                    className="w-full h-full object-cover"
                  />
                  {map.locked && (
                    <Lock className="absolute top-0.5 right-0.5 w-3 h-3 text-gray-900/75" />
                  )}
                </div>
                {!compareMode && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePin(map.id, false, map.kind);
                    }}
                    disabled={pinningId === map.id}
                    title="Unpin"
                    className="absolute bottom-0.5 right-0.5 h-6 w-6 rounded-full bg-gray-900/75 hover:bg-red-600 text-white flex items-center justify-center"
                  >
                    <PinOff className="h-3 w-3" />
                  </button>
                )}
                {compareMode && isCompareSelected && (
                  <div className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-[10px]">
                    {compareIds.indexOf(map.id) + 1}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-gray-500 mt-2">Pinned ({pinnedList.length}/3)</p>
      </div>

      {/* Share-style dialog (only opens for unlocked maps) */}
      {shareDialogMap && (
        <ShareStyleDialog
          open={!!shareDialogMap}
          onOpenChange={(o) => { if (!o) setShareDialogMap(null); }}
          settings={shareDialogMap.settings || shareDialogMap.format?.settings || {}}
          source={{ kind: shareDialogMap.kind || 'recent', id: shareDialogMap.id }}
          defaultStyleName={shareDialogMap.place_name || ''}
        />
      )}

      {/* Enlarged comparison overlay */}
      {compareEnlarged && compareMaps.length >= 2 && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setCompareEnlarged(false)}
        >
          <div
            className="bg-white p-4 rounded-2xl max-w-6xl w-full mx-4 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-semibold">Comparing {compareMaps.length} maps</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCompareEnlarged(false)}
                className="text-gray-500 hover:text-gray"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            {compareStripsInner('w-full max-h-[80vh]')}
          </div>
        </div>
      )}
    </div>
  );
};


const LoadingRaster = ({ total_cells, cells_to_fetch }) => {
  const rows = Math.ceil(Math.sqrt(total_cells * 0.75));
  const columns = Math.ceil(total_cells / rows);
  const cells = Array(total_cells).fill(null);

  return (
    <div className="absolute inset-4 grid gap-1 relative"
      style={{
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridAutoRows: '1fr',
      }}
    >
      {cells.map((_, index) => {
        const isLoading = index < cells_to_fetch;
        return (
          <div
            key={index}
            className={`w-full h-full rounded-lg ${isLoading ? 'bg-yellow-500/20' : 'bg-green-500/20'
              } flex items-center justify-center`}
          >
            {isLoading && (
              <Loader className="w-1/3 h-1/3 animate-spin opacity-50 text-yellow-500" />
            )}
          </div>
        );
      })}
    </div>
  );
};

const calculateEstimatedTime = (cells_info, total_cells, isCached = false) => {
  let totalWeight = 0;
  const baseTimePerCell = 0.15;

  if (!isCached) {
    const hasStreetsAll = cells_info.streets_all > 0;
    const hasStreetsRoads = cells_info.streets_roads > 0;

    if (hasStreetsAll) {
      totalWeight += cells_info.streets_all * 0.20;
    } else if (hasStreetsRoads) {
      totalWeight += cells_info.streets_roads * 0.10;
    }

    if (cells_info.building > 0) {
      if (hasStreetsAll) {
        totalWeight += cells_info.building * 0.15;
      } else if (hasStreetsRoads) {
        totalWeight += cells_info.building * 0.2;
      } else {
        totalWeight += cells_info.building * 0.3;
      }
    }

    if (cells_info.forests > 0) totalWeight += cells_info.forests * 0.05;
    if (cells_info.naturalareas > 0) totalWeight += cells_info.naturalareas * 0.05;
    if (cells_info.water_areas > 0) totalWeight += cells_info.water_areas * 0.05;
  }

  let totalTime = total_cells * baseTimePerCell + totalWeight;
  if (isCached) {
    totalTime = totalTime * 0.1;
  }

  return Math.max(0.5, Math.ceil(totalTime * 2) / 2);
};

const formatTime = (minutes) => {
  if (minutes < 1) return `< ${minutes * 60} seconds`;
  if (minutes === 1) return '< 1 minute';
  if (Number.isInteger(minutes)) return `< ${minutes} minutes`;
  return `< ${minutes.toFixed(1)} minutes`;
};


export default RecentMaps;
